import React, { useState, useRef, useEffect, useId } from "react";
import { useTranslation } from "react-i18next";
import { Users } from "lucide-react";
import { tierColors } from "../../../utils/credibilityTiers";

// 2026-07-19 credibility-chip consolidation: this is a GROUP-wide average,
// a fundamentally different thing from an individual user's credibility
// chip (CredibilityChip.jsx) -- it used to render with the same Star icon
// and no distinguishing text, so a group's average and a member's own
// score looked identical at a glance. Now visually distinct: Users icon
// (not Star), and a visible "Group Avg" text qualifier, not just a tooltip.
//
// 2026-07-19b -- a second, separate bug found during a doc-validation pass:
// the color bands below used to be calibrated to a 0-100 scale (>=80 green,
// >=50 blue), but `average_credibility` is a straight mean of `global_score`,
// which runs on the real ~0-1000 scale (see credibility_service.py's
// derive_status_tier() / Technical_Docs/26_CREDIBILITY_SYSTEM_FULL_REFERENCE.md
// -- cold-start floor alone is 50). Any group with real, non-floor scores
// showed green almost immediately regardless of actual quality. Now derives
// the same tier bands the backend uses (450/650/800/900) and reuses
// tierColors() -- the same palette CredibilityChip.jsx renders individual
// scores with -- so a group average visually reads against the identical
// scale a viewer already recognizes from per-user chips.
//
// 2026-09-11 -- the explanation was a native `title` attribute, which needs
// a slow hover dwell on desktop and is completely unreachable on mobile (no
// hover state on touch at all) -- found live during a design audit. The
// whole chip is now the tap/click target for a real popover, matching the
// InfoTooltip primitive's interaction model without needing a second icon
// squeezed into an already-compact badge.
const deriveTierFromScore = (score) => {
  if (score >= 900) return "Top Emperor";
  if (score >= 800) return "Elite";
  if (score >= 650) return "Advanced";
  if (score >= 450) return "Active";
  return "Beginner";
};

const GroupCredibilityChip = ({ credibility, compact = false }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const tooltipId = useId();

  useEffect(() => {
    if (!open) return;
    const handlePointer = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  if (!credibility) return null;

  const avg = Number.isFinite(credibility.average_credibility)
    ? credibility.average_credibility
    : 0;
  const total = Number.isFinite(credibility.total_credibility)
    ? credibility.total_credibility
    : 0;
  const memberCount = Number.isFinite(credibility.member_count)
    ? credibility.member_count
    : null;

  const tier = deriveTierFromScore(avg);
  const colors = tierColors(tier);
  const iconColor = colors.text;

  return (
    <span ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-expanded={open}
        aria-describedby={open ? tooltipId : undefined}
        className={`px-3 py-1.5 rounded-full backdrop-blur-md ${colors.bg} ${colors.border} ${colors.text}
          border shadow-sm flex items-center gap-2 transition-all duration-300 hover:scale-105 hover:shadow-md`}
      >
        <Users className={`w-4 h-4 opacity-80 ${iconColor}`} />
        {!compact && (
          <span className="text-[10px] uppercase tracking-wide opacity-60">{t('groups.credibilityChip.groupAvg')}</span>
        )}
        <span className="text-sm font-semibold">{avg.toFixed(1)}</span>
        {!compact && (
          <span className="text-xs opacity-80">({total.toFixed(0)} pts)</span>
        )}
      </button>
      {open && (
        <div
          id={tooltipId}
          role="tooltip"
          onClick={(e) => e.stopPropagation()}
          className="absolute z-50 top-full mt-2 left-0 w-64 max-w-[80vw] p-3 rounded-xl text-xs leading-relaxed
            bg-white text-gray-700 border border-gray-200 shadow-xl
            dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700"
        >
          {t('groups.credibilityChip.tooltip', { avg: avg.toFixed(1), tier, total: total.toFixed(0), memberCount: memberCount ?? '?' })}
        </div>
      )}
    </span>
  );
};

export default GroupCredibilityChip;
